import React, { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import Title from './title';
import { Grid, Paragraph } from './UIKit/system';
import Button from './button';
import Container from './container';
import { getThemeColor } from '../utils/styles';
import ReactSwiper from './react-swiper';
import IconButton from './icon-button';
import mq from '../utils/mq';

interface RailsSectionProps {
  title?: string;
  text?: string;
  cta?: {
    to: string;
    title: string;
  };
  backgroundColor?: string;
  children?: ReactNode;
  dark?: boolean;
  large?: boolean;
}

const RailsSection: FC<RailsSectionProps> = (props) => {
  const { title, text, cta, dark, children, large = true } = props;
  const backgroundColor = dark ? 'black' : undefined;
  const titleColor = dark ? 'white' : null;
  const textColor = dark ? 'white' : 'dark';
  const swiperOptions = {
    spaceBetween: 18,
    slidesPerView: 1,
    speed: 1000,
    navigation: {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next'
    }
  };
  const hasSlides = Array.isArray(children) && children.length > 1;

  return (
    <StyledSection backgroundColor={backgroundColor}>
      <Container large={large} pt={[48, 96]} pb={[48, 96]}>
        <StyledHeader>
          <div>
            {title && (
              <Title as="h2" color={titleColor}>
                {title}
              </Title>
            )}
            {text && (
              <Paragraph color={textColor} pt={16}>
                {text}
              </Paragraph>
            )}
          </div>
          {cta && (
            <Button
              to={cta.to}
              borderColorPrimary={dark}
              alignSelf="end"
              justifySelf="end"
            >
              {cta.title}
            </Button>
          )}
        </StyledHeader>
        {hasSlides ? (
          <>
            <ReactSwiper options={swiperOptions} mt={[33, 67]}>
              {React.Children.map(children, (child) => (
                <Slide key={child?.key}>{child}</Slide>
              ))}
            </ReactSwiper>
            <SwiperNav>
              <IconButton
                icon={{ name: 'chevronLeft2', strokeColor: 'white' }}
                className="swiper-button-prev"
              />
              <IconButton
                icon={{ name: 'chevronRight2', strokeColor: 'white' }}
                className="swiper-button-next"
              />
            </SwiperNav>
          </>
        ) : (
          <Grid mt={[33, 67]}>{children}</Grid>
        )}
      </Container>
    </StyledSection>
  );
};

const StyledHeader = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 50%;

  ${mq.mobile(css`
    grid-auto-flow: row;
    grid-template-columns: none;
    gap: 30px;
  `)}
`;

interface StyledSectionProps {
  backgroundColor?: string;
}

const StyledSection = styled.section<StyledSectionProps>`
  overflow: hidden;

  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${getThemeColor(props.backgroundColor)(props)};
    `}
`;

const Slide = styled(ReactSwiper.Item)`
  height: auto;
`;

const SwiperNav = styled.div`
  display: grid;
  gap: 18px;
  grid-auto-flow: column;
  justify-content: start;
  margin-top: 32px;
`;

export default RailsSection;
